import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import './i18n';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import ProtectedRoute, { invalidateAuthCache } from './components/ProtectedRoute';
import { clearStoredAuth, supabase, syncSessionStorage } from './lib/supabase';
import { initThemeFromStorage } from './lib/theme';

initThemeFromStorage();
const LOCAL_DEV_TOKEN = 'local-dev-token';

// Lazy load all pages – only the current page's code is downloaded
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
const ImpressumPage = lazy(() => import('./pages/ImpressumPage'));
const DatenschutzPage = lazy(() => import('./pages/DatenschutzPage'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const UmsatzPage = lazy(() => import('./pages/UmsatzPage'));
const KundenPage = lazy(() => import('./pages/KundenPage'));
const ProduktePage = lazy(() => import('./pages/ProduktePage'));
const AngebotePage = lazy(() => import('./pages/AngebotePage'));
const RechnungenPage = lazy(() => import('./pages/RechnungenPage'));
const RechnungXmlVorschauPage = lazy(() => import('./pages/RechnungXmlVorschauPage'));
const EingangsrechnungenPage = lazy(() => import('./pages/EingangsrechnungenPage'));
const EingangsrechnungDetailPage = lazy(() => import('./pages/EingangsrechnungDetailPage'));
const RechnungErstellenPage = lazy(() => import('./pages/RechnungErstellenPage'));
const RechnungDetailPage = lazy(() => import('./pages/RechnungDetailPage'));
const EinstellungenPage = lazy(() => import('./pages/EinstellungenPage'));

function PageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="w-10 h-10 border-3 border-accent/20 border-t-accent rounded-full animate-spin" />
    </div>
  );
}

function RootRoute() {
  const [ready, setReady] = useState(() => !supabase);
  const [hasSession, setHasSession] = useState(() =>
    typeof window !== 'undefined' && Boolean(localStorage.getItem('token'))
  );

  useEffect(() => {
    let active = true;
    const hasLocalDevToken =
      typeof window !== 'undefined' && localStorage.getItem('token') === LOCAL_DEV_TOKEN;

    if (!supabase) {
      setReady(true);
      return () => {
        active = false;
      };
    }

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) {
          return;
        }
        if (data.session?.access_token) {
          syncSessionStorage(data.session);
          setHasSession(true);
          return;
        }
        setHasSession(hasLocalDevToken);
      })
      .catch(() => {
        if (active) {
          setHasSession(
            typeof window !== 'undefined' && Boolean(localStorage.getItem('token'))
          );
        }
      })
      .finally(() => {
        if (active) {
          setReady(true);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  if (!ready) {
    return <PageFallback />;
  }

  return hasSession ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

function AppRouter() {
  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      <Route path="/datenschutz" element={<DatenschutzPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={
        <ProtectedRoute redirectIfUnternehmen><OnboardingPage /></ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute requireUnternehmen requireSubscription><DashboardPage /></ProtectedRoute>
      } />
      <Route path="/analyse" element={
        <ProtectedRoute requireUnternehmen requireSubscription><UmsatzPage /></ProtectedRoute>
      } />
      <Route path="/umsatz" element={<Navigate to="/analyse" replace />} />
      <Route path="/kunden" element={
        <ProtectedRoute requireUnternehmen requireSubscription><KundenPage /></ProtectedRoute>
      } />
      <Route path="/produkte" element={
        <ProtectedRoute requireUnternehmen requireSubscription><ProduktePage /></ProtectedRoute>
      } />
      <Route path="/angebote" element={
        <ProtectedRoute requireUnternehmen><AngebotePage /></ProtectedRoute>
      } />
      <Route path="/angebote/neu" element={
        <ProtectedRoute requireUnternehmen requireSubscription><RechnungErstellenPage mode="angebot" /></ProtectedRoute>
      } />
      <Route path="/angebote/:id/bearbeiten" element={
        <ProtectedRoute requireUnternehmen requireSubscription><RechnungErstellenPage mode="angebot" /></ProtectedRoute>
      } />
      <Route path="/angebote/:id" element={
        <ProtectedRoute requireUnternehmen><RechnungDetailPage /></ProtectedRoute>
      } />
      <Route path="/rechnungen" element={
        <ProtectedRoute requireUnternehmen><RechnungenPage /></ProtectedRoute>
      } />
      <Route path="/rechnungen/xml-vorschau" element={
        <ProtectedRoute requireUnternehmen><RechnungXmlVorschauPage /></ProtectedRoute>
      } />
      <Route path="/eingangsrechnungen" element={
        <ProtectedRoute requireUnternehmen><EingangsrechnungenPage /></ProtectedRoute>
      } />
      <Route path="/eingangsrechnungen/:id" element={
        <ProtectedRoute requireUnternehmen><EingangsrechnungDetailPage /></ProtectedRoute>
      } />
      <Route path="/rechnungen/neu" element={
        <ProtectedRoute requireUnternehmen requireSubscription><RechnungErstellenPage mode="rechnung" /></ProtectedRoute>
      } />
      <Route path="/rechnungen/:id/bearbeiten" element={
        <ProtectedRoute requireUnternehmen requireSubscription><RechnungErstellenPage mode="rechnung" /></ProtectedRoute>
      } />
      <Route path="/rechnungen/:id" element={
        <ProtectedRoute requireUnternehmen><RechnungDetailPage /></ProtectedRoute>
      } />
      <Route path="/einstellungen" element={
        <ProtectedRoute requireUnternehmen><EinstellungenPage /></ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    if (!supabase) {
      return undefined;
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        syncSessionStorage(session);
      } else {
        if (typeof window !== 'undefined' && localStorage.getItem('token') === LOCAL_DEV_TOKEN) {
          return;
        }
        invalidateAuthCache();
        clearStoredAuth();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <BrowserRouter>
      <SubscriptionProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </SubscriptionProvider>
    </BrowserRouter>
  );
}

export default App;
